/**
 * Send Inquiry - Product Info 表单区块
 * 参考 Figma node-id=6562-113546, 6562-114181 (Optional Fields 展开)
 * 使用 @leadsnavi/ui-kit 组件，100% 还原 Figma UI
 */

import React, { useState } from 'react';
import {
  Input,
  Textarea,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@leadsnavi/ui-kit';
import { ChevronDown, ChevronUp } from 'lucide-react';
import cn from 'classnames';
import type { SendInquiryProductInfo } from './types';

const UNIT_OPTIONS = [
  "20' Container",
  "40' Container",
  "40' HQ Container",
  'Piece(s)',
  'Bag(s)',
  'Box(es)',
  'Foot(Feet)',
  'Meter(s)',
  'Pair(s)',
  'Ream(s)',
  'Roll(s)',
  'Set(s)',
  'Square Meter(s)',
  'Square Foot(Feet)',
  'Ton(s)',
  'Yard(s)',
  'Other',
];

const TRADE_TERMS_OPTIONS = ['FOB', 'EXW', 'FCA', 'FAS', 'CFR', 'CIF', 'CPT', 'CIP', 'DAT', 'DDP', 'DAU', 'DAP'];

// const CURRENCY_OPTIONS = ['USD', 'EUR', 'GBP', 'RMB', 'AUD', 'CAD', 'CHF', 'JPY', 'HKD', 'NZD', 'SGD', 'NTD', 'Other'];

const SHIPPING_METHOD_OPTIONS = ['Sea Freight', 'Air Freight', 'Express', 'Land Freight'];

const PAYMENT_METHOD_OPTIONS = ['LC', 'T/T', 'D/P', 'W/U', 'Other'];

// const VALID_TO_DAYS_OPTIONS = ['30 days', '60 days', '90 days'];

interface SendInquiryProductInfoSectionProps {
  productInfo: SendInquiryProductInfo;
  onChange: (info: SendInquiryProductInfo) => void;
  errors: Record<string, string>;
}

/** Figma: Input Regular - padding 7.5px 12px, borderRadius 8px, border #D4D4D4, shadow-xs */
const INPUT_CLASS =
  'h-9 rounded-[8px] border border-[#D4D4D4] px-3 py-[7.5px] text-sm shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] placeholder:text-[#A3A3A3]';

/** Figma: Label - paragraph small/medium, 14px, 500 weight, #171717 */
const LABEL_CLASS = 'text-sm font-medium text-[#171717]';

/** Figma: Vertical Field - gap 4px between label and input */
const FIELD_CLASS = 'flex flex-col gap-1';

/** Figma: SelectTrigger 样式 - 与 Input 一致 */
const SELECT_TRIGGER_CLASS =
  'h-9 w-full rounded-[8px] border border-[#D4D4D4] px-3 py-[7.5px] text-sm shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] [&>span]:text-[#171717] data-[placeholder]:text-[#A3A3A3]';

function FormSelect({
  value,
  options,
  onValueChange,
  placeholder,
  className,
  contentClassName,
  error,
}: {
  value: string;
  options: string[];
  onValueChange: (v: string) => void;
  placeholder?: string;
  className?: string;
  contentClassName?: string;
  error?: boolean;
}) {
  return (
    <Select value={value || undefined} onValueChange={onValueChange}>
      <SelectTrigger className={cn(SELECT_TRIGGER_CLASS, error && 'border-red-500', className)}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent
        className={cn('max-h-[200px] rounded-[8px] border border-[#D4D4D4] shadow-md', contentClassName)}
        sideOffset={4}
      >
        {options.map(opt => (
          <SelectItem key={opt} value={opt} className="text-sm">
            {opt}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const SendInquiryProductInfoSection: React.FC<SendInquiryProductInfoSectionProps> = ({
  productInfo,
  onChange,
  errors,
}) => {
  const [optionalOpen, setOptionalOpen] = useState(false);

  return (
    <div className="flex flex-col gap-6 px-5 py-5 pb-7">
      {/* Figma: Form layout - gap 24px, padding 20px 20px 28px */}
      <div className="flex flex-col gap-6">
        {/* Row 1: Product Name* - full width (Figma 7621-21230) */}
        {/* <div className="grid grid-cols-1 sm:grid-cols-2 gap-6"> */}
        <div className={FIELD_CLASS}>
          <Label htmlFor="product-name" className={LABEL_CLASS}>
            Product Name<span className="text-red-500 ml-0.5">*</span>
          </Label>
          <Input
            id="product-name"
            value={productInfo.productName}
            onChange={e => onChange({ ...productInfo, productName: e.target.value })}
            placeholder="e.g.,Water bottle"
            maxLength={100}
            className={cn(INPUT_CLASS, errors.productName && 'border-red-500')}
          />
          {errors.productName && <p className="text-red-500 text-xs mt-1">{errors.productName}</p>}
        </div>
        {/* </div> */}

        {/* Row 3: Trade Terms* | Preferred Unit Price* - 2 columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className={FIELD_CLASS}>
            <Label className={LABEL_CLASS}>
              Trade Terms<span className="text-red-500 ml-0.5">*</span>
            </Label>
            <FormSelect
              value={productInfo.tradeTerms || ''}
              options={TRADE_TERMS_OPTIONS}
              onValueChange={v => onChange({ ...productInfo, tradeTerms: v })}
              placeholder="FOB"
              error={!!errors.tradeTerms}
            />
            {errors.tradeTerms && <p className="text-red-500 text-xs mt-1">{errors.tradeTerms}</p>}
          </div>

          <div className={FIELD_CLASS}>
            <Label htmlFor="preferred-unit-price" className={LABEL_CLASS}>
              Preferred Unit Price<span className="text-red-500 ml-0.5">*</span>
            </Label>
            <div
              className={cn(
                'flex items-center justify-between gap-2 rounded-[8px] border px-3 py-[7.5px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] bg-white h-9',
                errors.preferredUnitPrice ? 'border-red-500' : 'border-[#D4D4D4]'
              )}
            >
              <div className="flex-1">
                <Input
                  id="preferred-unit-price"
                  value={productInfo.preferredUnitPrice}
                  onChange={e => {
                    const value = e.target.value;
                    // 只允许纯数字，不允许小数点
                    if (value === '' || /^\d+$/.test(value)) {
                      if (value.length <= 16) {
                        onChange({ ...productInfo, preferredUnitPrice: value });
                      }
                    }
                  }}
                  maxLength={16}
                  className="flex-1 w-full min-w-0 h-auto border-0 p-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none"
                />
              </div>
              <span className="text-sm text-[#A3A3A3] shrink-0">USD</span>
            </div>
            {errors.preferredUnitPrice && <p className="text-red-500 text-xs mt-1">{errors.preferredUnitPrice}</p>}
          </div>
        </div>
        {/* Row 2: Order Quantity* | Max Budget* - 2 columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className={FIELD_CLASS}>
            <Label htmlFor="order-quantity" className={LABEL_CLASS}>
              Order Quantity<span className="text-red-500 ml-0.5">*</span>
            </Label>
            <div className="flex gap-2 flex-1">
              <div className="flex-1">
                <Input
                  id="order-quantity"
                  value={productInfo.orderQuantity}
                  onChange={e => onChange({ ...productInfo, orderQuantity: e.target.value })}
                  type="number"
                  min={0}
                  className={cn('flex-1', INPUT_CLASS, errors.orderQuantity && 'border-red-500')}
                />
              </div>
              <FormSelect
                value={productInfo.unitCode || ''}
                options={UNIT_OPTIONS}
                onValueChange={v => onChange({ ...productInfo, unitCode: v })}
                placeholder="Piece(s)"
                className="w-[180px] shrink-0"
                contentClassName="w-[180px]"
                error={!!errors.orderQuantity}
              />
            </div>
            {errors.orderQuantity && <p className="text-red-500 text-xs mt-1">{errors.orderQuantity}</p>}
          </div>

          <div className={FIELD_CLASS}>
            <Label htmlFor="max-budget" className={LABEL_CLASS}>
              Max Budget<span className="text-red-500 ml-0.5">*</span>
            </Label>
            <div
              className={cn(
                'flex items-center justify-between gap-2 rounded-[8px] border px-3 py-[7.5px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] bg-white h-9',
                errors.maxBudget ? 'border-red-500' : 'border-[#D4D4D4]'
              )}
            >
              <div className="flex-1">
                <Input
                  id="max-budget"
                  value={productInfo.maxBudget}
                  onChange={e => {
                    const value = e.target.value;
                    // 只允许纯数字，不允许小数点
                    if (value === '' || /^\d+$/.test(value)) {
                      if (value.length <= 16) {
                        onChange({ ...productInfo, maxBudget: value });
                      }
                    }
                  }}
                  maxLength={16}
                  className="flex-1 w-full min-w-0 h-auto border-0 p-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none"
                />
              </div>
              <span className="text-sm text-[#A3A3A3] shrink-0">USD</span>
            </div>
            {errors.maxBudget && <p className="text-red-500 text-xs mt-1">{errors.maxBudget}</p>}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className={FIELD_CLASS}>
            <Label htmlFor="specifications" className={LABEL_CLASS}>
              Specifications
            </Label>
            <Input
              id="specifications"
              value={productInfo.specifications}
              onChange={e => onChange({ ...productInfo, specifications: e.target.value })}
              placeholder="Handa"
              maxLength={500}
              className={INPUT_CLASS}
            />
          </div>
          <div className={FIELD_CLASS}>
            <Label htmlFor="model-no" className={LABEL_CLASS}>
              Model No.
            </Label>
            <Input
              id="model-no"
              value={productInfo.modelNo}
              onChange={e => onChange({ ...productInfo, modelNo: e.target.value })}
              placeholder="Esp32-S3"
              maxLength={100}
              className={INPUT_CLASS}
            />
          </div>
        </div>
        {/* Row 4: Details* - full width */}
        <div className={FIELD_CLASS}>
          <Label htmlFor="details" className={LABEL_CLASS}>
            Details<span className="text-red-500 ml-0.5">*</span>
          </Label>
          <Textarea
            id="details"
            value={productInfo.details}
            onChange={e => onChange({ ...productInfo, details: e.target.value })}
            placeholder="Describe the product you want to source. You may include color, material, size, weight, packaging, certification requirements, and other details."
            rows={4}
            maxLength={4000}
            className={cn(
              'rounded-[8px] border border-[#D4D4D4] px-3 py-[7.5px] text-sm shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] placeholder:text-[#A3A3A3] min-h-[80px] resize-none',
              errors.details && 'border-red-500'
            )}
          />
          {errors.details && <p className="text-red-500 text-xs mt-1">{errors.details}</p>}
        </div>

        {/* Row 5: Optional Fields - Collapsible */}
        <Collapsible open={optionalOpen} onOpenChange={setOptionalOpen}>
          <CollapsibleTrigger className="flex items-center gap-[11px] w-full cursor-pointer">
            <div className="flex items-center gap-0.5">
              {optionalOpen ? (
                <ChevronUp size={16} className="text-[#525252]" />
              ) : (
                <ChevronDown size={16} className="text-[#525252]" />
              )}
              <span className="text-sm text-[#737373]">Optional Fields</span>
            </div>
            <div className="flex-1 h-px bg-[#E5E5E5] min-w-0" />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="flex flex-col gap-6 pt-6">
              {/* Row 1: Specifications | Model No. - 2 columns (Figma 7621-21230) */}

              {/* Row 2: Shipping Method | Payment Method - 2 columns */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className={FIELD_CLASS}>
                  <Label className={LABEL_CLASS}>Shipping Method</Label>
                  <FormSelect
                    value={productInfo.shippingMethod || ''}
                    options={SHIPPING_METHOD_OPTIONS}
                    onValueChange={v => onChange({ ...productInfo, shippingMethod: v })}
                    placeholder="Sea freight"
                  />
                </div>
                <div className={FIELD_CLASS}>
                  <Label className={LABEL_CLASS}>Payment Method</Label>
                  <FormSelect
                    value={productInfo.paymentMethod || ''}
                    options={PAYMENT_METHOD_OPTIONS}
                    onValueChange={v => onChange({ ...productInfo, paymentMethod: v })}
                    placeholder="T/T"
                  />
                </div>
              </div>
              {/* Row 3: Lead Time - full width (Figma: Ship in + input + day(s) after...) */}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    </div>
  );
};

export default SendInquiryProductInfoSection;
